import { createSlice } from '@reduxjs/toolkit';
import { makeSpecialPanelConditions } from '@functional';
import {
  fetchSavedDeliverySettings,
  fetchEstimatedDeliveryTargetCounts,
  logoutAdmin,
  logoutUser,
} from '~/api';
import { AGE_DIVIDE_TYPES } from '~/constants';

const initialState = {
  current: {
    gender: null,
    age: {
      from: null,
      to: null,
      divideType: null,
    },
    areas: {
      checkedAreaCds: [],
    },
    specialConditions: {
      checkedItems: {},
      forApi: [],
    },
  },

  // 対象者検索を実行した時の条件
  onSearched: {
    gender: null,
    age: {
      from: null,
      to: null,
      divideType: null,
    },
    areas: {
      checkedAreaCds: [],
    },
    specialConditions: {
      checkedItems: {},
      forApi: [],
    },
  },
};

const qualificationsSlice = createSlice({
  name: 'qualifications',
  initialState,
  reducers: {
    initialize: () => initialState,
    changed: (state, action) => {
      return {
        ...state,
        current: {
          ...state.current,
          [action.payload.key]: action.payload.value,
        },
      };
    },

    /**
     * スペパネ選択肢の選択状態更新用Action。
     *
     * 他のスペパネ選択肢の選択状態の変化が各スペパネ設問Componentに影響を与えないよう、
     * changedではなくスペパネ用のactionを用意する。
     * （changedでやろうとすると、component側でcurrent.specialConditionsを全て参照することになり、
     * 関係ないスペパネ設問の変更の影響を受けるため）
     */
    changedSpecialPanelCheckedItems: (state, action) => {
      const newCheckedItems = {
        ...state.current.specialConditions.checkedItems,
        [action.payload.key]: action.payload.value,
      };
      return {
        ...state,
        current: {
          ...state.current,
          specialConditions: {
            ...state.current.specialConditions,
            checkedItems: {
              ...state.current.specialConditions.checkedItems,
              [action.payload.key]: action.payload.value,
            },
            forApi: makeSpecialPanelConditions(
              newCheckedItems,
              action.payload.specialPanelDisplaySettingId
            ),
          },
        },
      };
    },
  },
  // 非同期はextraReducersで行う
  extraReducers: {
    [fetchSavedDeliverySettings.fulfilled.type]: (state, action) => {
      if (action.payload.qualifications === null) {
        return state;
      }

      return {
        ...state,
        current: {
          gender: action.payload.qualifications.gender,
          age: {
            from: action.payload.qualifications.ageFrom,
            to: action.payload.qualifications.ageTo,
            divideType: action.payload.qualifications.isDivideAge
              ? AGE_DIVIDE_TYPES.TEN
              : AGE_DIVIDE_TYPES.NONE,
          },
          areas: {
            checkedAreaCds: action.payload.qualifications.region,
          },
          specialConditions: {
            checkedItems: action.payload.qualifications.specialConditions.checkedItems,
            forApi: action.payload.qualifications.specialConditions.forApi,
          },
        },
      };
    },

    /**
     * 対象者検索ボタンがクリックされた時にトリガーされるAction後のstate更新。
     * 対象者検索時の検索条件を保持しておく。
     */
    [fetchEstimatedDeliveryTargetCounts.fulfilled.type]: (state) => {
      return {
        ...state,
        onSearched: JSON.parse(JSON.stringify(state.current)), // ネストされたobjectをコピー
      };
    },

    [logoutAdmin.fulfilled.type]: () => {
      return initialState;
    },

    [logoutUser.fulfilled.type]: () => {
      return initialState;
    },
  },
});

// sliceで定義したactionは全てexport
export const { changed, changedSpecialPanelCheckedItems, initialize } = qualificationsSlice.actions;

// reducerをexportする
export default qualificationsSlice.reducer;
